/**
 * DiscoveryFlow - Multi-step discovery component
 *
 * Steps:
 * 1. Playback - AI summarizes what it understood
 * 2. Assumptions - User confirms/edits inferred assumptions
 * 3. Risks - User reviews risks and considerations
 * 4. Scoping - User assigns features to phases (after spec generation)
 */
import React, { useState, useMemo } from 'react';
import { usePostHog } from 'posthog-js/react';
import {
  Card,
  Button,
  Text,
  Caption,
  Badge,
  tokens,
} from '../design-system';

const T = {
  paper:     '#F7EFD8',
  paperDeep: '#EEE2BF',
  paperSoft: '#FDF7E5',
  card:      '#FFFBEC',
  ink:       '#2E2109',
  ink2:      '#4A3510',
  inkMuted:  '#6E5A38',
  inkFaint:  '#A39068',
  rule:      '#D7C9A1',
  ruleStrong:'#BFAB79',
  orange:    '#EA7C1F',
  orangeDark:'#C25F0E',
  moss:      '#6B7A3E',
  capRed:    '#C14A33',
  r2: '6px', r3: '10px', r4: '14px', full: '999px',
  sh1: '0 1px 0 rgba(46,33,9,0.06), 0 2px 4px rgba(46,33,9,0.04)',
  sh2: '0 2px 0 rgba(46,33,9,0.04), 0 8px 16px -8px rgba(46,33,9,0.14)',
  sans: '"Nunito", ui-sans-serif, system-ui, -apple-system, sans-serif',
  hand: '"Caveat", "Nunito", cursive',
  mono: '"JetBrains Mono", ui-monospace, "SF Mono", Menlo, monospace',
};

// ============================================================================
// Step indicators
// ============================================================================
const STEPS = [
  { id: 'playback', label: 'Review', icon: '1' },
  { id: 'assumptions', label: 'Assumptions', icon: '2' },
  { id: 'risks', label: 'Risks', icon: '3' },
  { id: 'scoping', label: 'Scope', icon: '4' },
];

// spec_review is a transient loading state between risks and scoping — not a visible step
const STEP_IDS_FOR_INDICATOR = ['playback', 'assumptions', 'risks', 'scoping'];

const StepIndicator = ({ currentStep }) => {
  // spec_review maps to the risks step visually (it's a transient background state)
  const indicatorStep = currentStep === 'spec_review' ? 'risks' : currentStep;
  const currentIndex = STEPS.findIndex(s => s.id === indicatorStep);
  return (
    <div style={{
      display: 'flex',
      gap: tokens.spacing[1],
      marginBottom: tokens.spacing[5],
      width: '100%',
    }}>
      {STEPS.map((step, i) => {
        const isComplete = i < currentIndex;
        const isCurrent = i === currentIndex;
        return (
          <div key={step.id} style={{
            display: 'flex',
            alignItems: 'center',
            gap: tokens.spacing[2],
            flex: 1,
          }}>
            <div style={{
              width: 28,
              height: 28,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: tokens.typography.fontSize.xs[0],
              fontWeight: tokens.typography.fontWeight.bold,
              backgroundColor: isComplete
                ? T.moss
                : isCurrent
                  ? T.orange
                  : T.rule,
              color: isComplete || isCurrent ? '#fff' : T.inkMuted,
              flexShrink: 0,
            }}>
              {isComplete ? '\u2713' : step.icon}
            </div>
            <Caption
              className="hidden sm:block"
              style={{
                margin: 0,
                color: isCurrent ? T.ink : T.inkMuted,
                fontWeight: isCurrent ? tokens.typography.fontWeight.medium : 'normal',
              }}
            >
              {step.label}
            </Caption>
            {i < STEPS.length - 1 && (
              <div style={{
                flex: 1,
                height: 2,
                backgroundColor: isComplete ? T.moss : T.rule,
              }} />
            )}
          </div>
        );
      })}
    </div>
  );
};

// ============================================================================
// Step 1: Playback
// ============================================================================
const PlaybackStep = ({ playback, onConfirm, onEdit, isLoading }) => {
  const [editMode, setEditMode] = useState(false);
  const [editedPlayback, setEditedPlayback] = useState(playback || {});

  // Sync when playback arrives
  React.useEffect(() => {
    if (playback) setEditedPlayback(playback);
  }, [playback]);

  if (isLoading || !playback) {
    return (
      <Card padding="lg" style={{ backgroundColor: T.card, border: `1px solid ${T.rule}`, boxShadow: T.sh1 }}>
        <div style={{ textAlign: 'center', padding: tokens.spacing[8] }}>
          <div style={{ fontSize: '24px', marginBottom: tokens.spacing[3] }}>
            {'\uD83D\uDD0D'}
          </div>
          <Text style={{ color: T.inkMuted }}>
            Analyzing your idea...
          </Text>
        </div>
      </Card>
    );
  }

  if (editMode) {
    // Editable features list — key_features are objects {name, implemented}
    const editFeatures = (editedPlayback.key_features || []).map(f =>
      typeof f === 'string' ? { name: f, implemented: false } : f
    );
    const updateFeature = (idx, value) => {
      const updated = [...editFeatures];
      updated[idx] = { ...updated[idx], name: value };
      setEditedPlayback(p => ({ ...p, key_features: updated }));
    };
    const removeFeature = (idx) => {
      setEditedPlayback(p => ({ ...p, key_features: editFeatures.filter((_, i) => i !== idx) }));
    };
    const addFeature = () => {
      setEditedPlayback(p => ({ ...p, key_features: [...editFeatures, { name: '', implemented: false }] }));
    };

    return (
      <Card padding="lg" style={{ backgroundColor: T.card, border: `1px solid ${T.rule}`, boxShadow: T.sh1 }}>
        <Text style={{
          fontWeight: tokens.typography.fontWeight.semibold,
          marginBottom: tokens.spacing[4],
          color: T.ink,
        }}>
          Edit what I understood
        </Text>

        {/* Summary */}
        <div style={{ marginBottom: tokens.spacing[4] }}>
          <label style={labelStyle}>Summary</label>
          <textarea
            value={editedPlayback.summary || ''}
            onChange={(e) => setEditedPlayback(p => ({ ...p, summary: e.target.value }))}
            rows={3}
            style={textareaStyle}
          />
        </div>

        {/* Core Value */}
        <div style={{ marginBottom: tokens.spacing[4] }}>
          <label style={labelStyle}>Core Value Proposition</label>
          <textarea
            value={editedPlayback.core_value_prop || ''}
            onChange={(e) => setEditedPlayback(p => ({ ...p, core_value_prop: e.target.value }))}
            rows={2}
            style={textareaStyle}
          />
        </div>

        {/* Target User */}
        <div style={{ marginBottom: tokens.spacing[4] }}>
          <label style={labelStyle}>Target User</label>
          <textarea
            value={editedPlayback.target_user || ''}
            onChange={(e) => setEditedPlayback(p => ({ ...p, target_user: e.target.value }))}
            rows={2}
            style={textareaStyle}
          />
        </div>

        {/* Key Features */}
        <div style={{ marginBottom: tokens.spacing[4] }}>
          <label style={labelStyle}>Key Features</label>
          {editFeatures.map((f, i) => (
            <div key={i} style={{
              display: 'flex',
              gap: tokens.spacing[2],
              marginBottom: tokens.spacing[2],
              alignItems: 'center',
            }}>
              <input
                value={f.name}
                onChange={(e) => updateFeature(i, e.target.value)}
                style={{
                  flex: 1,
                  padding: tokens.spacing[2],
                  backgroundColor: T.card,
                  border: `1px solid ${T.rule}`,
                  borderRadius: T.r3,
                  color: T.ink,
                  fontSize: tokens.typography.fontSize.sm[0],
                  fontFamily: 'inherit',
                }}
              />
              <button
                onClick={() => removeFeature(i)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: T.capRed,
                  cursor: 'pointer',
                  fontSize: '16px',
                  padding: tokens.spacing[1],
                }}
              >
                &times;
              </button>
            </div>
          ))}
          <button
            onClick={addFeature}
            style={{
              background: 'none',
              border: `1px dashed ${T.rule}`,
              borderRadius: T.r3,
              color: T.inkMuted,
              cursor: 'pointer',
              padding: `${tokens.spacing[2]} ${tokens.spacing[3]}`,
              fontSize: tokens.typography.fontSize.sm[0],
              width: '100%',
            }}
          >
            + Add feature
          </button>
        </div>

        <div style={{ display: 'flex', gap: tokens.spacing[2], justifyContent: 'flex-end' }}>
          <Button variant="ghost" size="sm" onClick={() => setEditMode(false)}>Cancel</Button>
          <Button variant="primary" size="sm" onClick={() => {
            const cleaned = {
              ...editedPlayback,
              key_features: editFeatures.filter(f => f.name.trim()),
            };
            onConfirm(cleaned);
            setEditMode(false);
          }}>Save & Continue</Button>
        </div>
      </Card>
    );
  }

  return (
    <Card padding="lg" style={{ backgroundColor: T.card, border: `1px solid ${T.rule}`, boxShadow: T.sh1 }}>
      <Text style={{
        fontWeight: tokens.typography.fontWeight.semibold,
        fontSize: tokens.typography.fontSize.lg[0],
        marginBottom: tokens.spacing[4],
        color: T.ink,
        fontFamily: T.hand,
      }}>
        Here's what I understood
      </Text>

      <Text style={{
        marginBottom: tokens.spacing[4],
        lineHeight: 1.7,
        color: T.ink,
      }}>
        {playback.summary}
      </Text>

      <div style={{ marginBottom: tokens.spacing[5] }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: tokens.spacing[2] }}>
          <Caption style={{ color: T.orange }}>
            Key Features Identified
          </Caption>
          {playback.existing_features && playback.existing_features.length > 0 && (
            <span style={{ fontSize: tokens.typography.fontSize.xs?.[0] || '11px', color: T.moss, display: 'flex', alignItems: 'center', gap: tokens.spacing[1] }}>
              <span>✓</span> already in codebase
            </span>
          )}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: tokens.spacing[2] }}>
          {(playback.key_features || []).map((f, i) => {
            const name = typeof f === 'string' ? f : f.name;
            const isImplemented = typeof f === 'string'
              ? (playback.existing_features || []).includes(f)
              : !!f.implemented;
            return (
              <span key={i} style={{
                backgroundColor: isImplemented ? 'transparent' : T.paperDeep,
                border: `1px solid ${isImplemented ? T.moss : T.rule}`,
                borderRadius: T.r3,
                padding: `${tokens.spacing[1]} ${tokens.spacing[3]}`,
                fontSize: tokens.typography.fontSize.sm[0],
                color: isImplemented ? T.moss : T.ink,
              }}>
                {isImplemented ? '✓ ' : ''}{name}
              </span>
            );
          })}
        </div>
      </div>

      {playback.tech_stack && Object.values(playback.tech_stack).some(v => v?.length > 0) && (
        <div style={{ marginBottom: tokens.spacing[5] }}>
          <Caption style={{ marginBottom: tokens.spacing[2], color: T.orange }}>
            Tech Stack
          </Caption>
          <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacing[2] }}>
            {Object.entries(playback.tech_stack).map(([category, items]) =>
              items && items.length > 0 ? (
                <div key={category} style={{ display: 'flex', alignItems: 'baseline', gap: tokens.spacing[2] }}>
                  <Text style={{
                    margin: 0,
                    fontSize: tokens.typography.fontSize.sm[0],
                    color: T.inkMuted,
                    textTransform: 'capitalize',
                    minWidth: '90px',
                    flexShrink: 0,
                  }}>
                    {category}
                  </Text>
                  <Text style={{ margin: 0, fontSize: tokens.typography.fontSize.sm[0], color: T.ink2 }}>
                    {items.join(', ')}
                  </Text>
                </div>
              ) : null
            )}
          </div>
        </div>
      )}

      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        paddingTop: tokens.spacing[4],
        borderTop: `1px solid ${T.rule}`,
      }}>
        <Button variant="ghost" size="sm" onClick={() => setEditMode(true)}>
          Let me adjust
        </Button>
        <Button variant="primary" onClick={() => onConfirm(playback)}>
          That's right
        </Button>
      </div>
    </Card>
  );
};

// ============================================================================
// Step 2: Assumptions
// ============================================================================
const ASSUMPTION_COLORS = {
  high: T.moss,
  medium: '#C07A1A',
  low: T.capRed,
};

const CONFIDENCE_LABELS = {
  high: 'likely true',
  medium: 'uncertain',
  low: 'risky',
};

const AssumptionsStep = ({ assumptions, onConfirm, onBack, isLoading }) => {
  const [items, setItems] = useState(assumptions || []);

  React.useEffect(() => {
    if (assumptions?.length) setItems(assumptions);
  }, [assumptions]);

  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [addingToCategory, setAddingToCategory] = useState(null);
  const [newAssumption, setNewAssumption] = useState('');
  const [newValue, setNewValue] = useState('');

  const handleAdd = (category) => {
    if (!newAssumption.trim()) return;
    const id = `a_${category}_custom_${Date.now()}`;
    setItems(prev => [...prev, {
      id,
      category,
      assumption: newAssumption.trim(),
      suggested_value: 'yes',
      confidence: 'high',
      source: 'Added by user',
      type: 'confirm',
    }]);
    setNewAssumption('');
    setNewValue('');
    setAddingToCategory(null);
  };

  const handleEditStart = (a) => {
    setEditingId(a.id);
    setEditValue(a.assumption || '');
  };

  const handleEditSave = (id) => {
    setItems(prev => prev.map(a =>
      a.id === id ? { ...a, assumption: editValue } : a
    ));
    setEditingId(null);
  };

  const handleDismiss = (id) => {
    setItems(prev => prev.filter(a => a.id !== id));
  };

  if (isLoading || !assumptions?.length) {
    return (
      <Card padding="lg" style={{ backgroundColor: T.card, border: `1px solid ${T.rule}`, boxShadow: T.sh1 }}>
        <div style={{ textAlign: 'center', padding: tokens.spacing[8] }}>
          <Text style={{ color: T.inkMuted }}>
            Identifying assumptions...
          </Text>
        </div>
      </Card>
    );
  }

  // Group by category
  const grouped = items.reduce((acc, a) => {
    const cat = a.category || 'general';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(a);
    return acc;
  }, {});

  return (
    <Card padding="lg" style={{ backgroundColor: T.card, border: `1px solid ${T.rule}`, boxShadow: T.sh1 }}>
      <Text style={{
        fontWeight: tokens.typography.fontWeight.semibold,
        fontSize: tokens.typography.fontSize.lg[0],
        marginBottom: tokens.spacing[2],
        color: T.ink,
        fontFamily: T.hand,
      }}>
        Here's what I'm assuming
      </Text>
      <Caption style={{ marginBottom: tokens.spacing[4], color: T.inkMuted }}>
        Review these assumptions. Edit or dismiss anything that's wrong.
      </Caption>

      {Object.entries(grouped).map(([category, catAssumptions]) => (
        <div key={category} style={{ marginBottom: tokens.spacing[4] }}>
          <Caption style={{
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            marginBottom: tokens.spacing[2],
            color: T.inkFaint,
          }}>
            {category.replace('_', ' ')}
          </Caption>

          {catAssumptions.map((a) => {
            const isEditing = editingId === a.id;
            return (
              <div key={a.id} style={{

                padding: tokens.spacing[3],
                backgroundColor: T.paperSoft,
                borderRadius: T.r3,
                border: `1px solid ${isEditing ? T.orange : T.rule}`,
                marginBottom: tokens.spacing[2],
              }}>
                {isEditing ? (
                  /* Edit mode */
                  <div>
                    <textarea
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      rows={3}
                      style={{
                        width: '100%',
                        padding: tokens.spacing[3],
                        backgroundColor: T.card,
                        border: `1px solid ${T.orange}`,
                        borderRadius: T.r3,
                        color: T.ink,
                        fontSize: tokens.typography.fontSize.sm[0],
                        resize: 'vertical',
                        fontFamily: 'inherit',
                        boxSizing: 'border-box',
                        lineHeight: 1.6,
                        outline: 'none',
                      }}
                      autoFocus
                    />
                    <div style={{
                      display: 'flex',
                      gap: tokens.spacing[2],
                      marginTop: tokens.spacing[2],
                      justifyContent: 'flex-end',
                    }}>
                      <Button variant="ghost" size="sm" onClick={() => setEditingId(null)}>Cancel</Button>
                      <Button variant="primary" size="sm" onClick={() => handleEditSave(a.id)}>Save</Button>
                    </div>
                  </div>
                ) : (
                  /* Display mode */
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: tokens.spacing[3] }}>
                      <Text style={{ margin: 0, fontSize: tokens.typography.fontSize.sm[0], fontWeight: tokens.typography.fontWeight.medium, color: T.ink }}>
                        {a.assumption}
                      </Text>
                      <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacing[2], flexShrink: 0 }}>
                        <span style={{
                          fontSize: tokens.typography.fontSize.xs[0],
                          color: ASSUMPTION_COLORS[a.confidence] || T.inkMuted,
                          padding: `2px ${tokens.spacing[2]}`,
                          borderRadius: T.full,
                          backgroundColor: `${ASSUMPTION_COLORS[a.confidence] || T.inkMuted}18`,
                        }}>
                          {CONFIDENCE_LABELS[a.confidence] || a.confidence}
                        </span>
                        <button
                          onClick={() => handleEditStart(a)}
                          style={{
                            background: 'none',
                            border: `1px solid ${T.rule}`,
                            borderRadius: T.r2,
                            color: T.ink2,
                            padding: `${tokens.spacing[1]} ${tokens.spacing[2]}`,
                            cursor: 'pointer',
                            fontSize: tokens.typography.fontSize.xs[0],
                          }}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDismiss(a.id)}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: T.inkMuted,
                            cursor: 'pointer',
                            fontSize: tokens.typography.fontSize.xs[0],
                            padding: `${tokens.spacing[1]} ${tokens.spacing[2]}`,
                          }}
                        >
                          Dismiss
                        </button>
                      </div>
                    </div>
                    {a.source && (
                      <Text style={{
                        margin: `${tokens.spacing[1]} 0 0 0`,
                        fontSize: tokens.typography.fontSize.xs[0],
                        color: T.inkFaint,
                      }}>
                        {a.source}
                      </Text>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {/* Add assumption form */}
          {addingToCategory === category ? (
            <div style={{
              padding: tokens.spacing[3],
              backgroundColor: T.paperSoft,
              borderRadius: T.r3,
              border: `1px solid ${T.orange}`,
              marginBottom: tokens.spacing[2],
            }}>
              <input
                type="text"
                value={newAssumption}
                onChange={(e) => setNewAssumption(e.target.value)}
                placeholder="State your assumption..."
                autoFocus
                style={{
                  width: '100%',
                  padding: tokens.spacing[2],
                  backgroundColor: T.card,
                  border: `1px solid ${T.rule}`,
                  borderRadius: T.r2,
                  color: T.ink,
                  fontSize: tokens.typography.fontSize.sm[0],
                  fontFamily: 'inherit',
                  outline: 'none',
                  boxSizing: 'border-box',
                  marginBottom: tokens.spacing[2],
                }}
              />
              <div style={{ display: 'flex', gap: tokens.spacing[2], marginTop: tokens.spacing[2], justifyContent: 'flex-end' }}>
                <Button variant="ghost" size="sm" onClick={() => { setAddingToCategory(null); setNewAssumption(''); setNewValue(''); }}>Cancel</Button>
                <Button variant="primary" size="sm" onClick={() => handleAdd(category)} disabled={!newAssumption.trim()}>Add</Button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setAddingToCategory(category)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: tokens.spacing[1],
                background: 'none',
                border: `1px dashed ${T.rule}`,
                borderRadius: T.r3,
                color: T.inkMuted,
                cursor: 'pointer',
                fontSize: tokens.typography.fontSize.xs[0],
                padding: `${tokens.spacing[2]} ${tokens.spacing[3]}`,
                width: '100%',
                justifyContent: 'center',
                transition: 'all 0.15s ease',
              }}
              onMouseEnter={(e) => { e.target.style.borderColor = T.orange; e.target.style.color = T.orange; }}
              onMouseLeave={(e) => { e.target.style.borderColor = T.rule; e.target.style.color = T.inkMuted; }}
            >
              + Add assumption
            </button>
          )}
        </div>
      ))}

      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        paddingTop: tokens.spacing[4],
        borderTop: `1px solid ${T.rule}`,
      }}>
        {onBack ? (
          <Button variant="ghost" size="sm" onClick={onBack}>Back</Button>
        ) : <div />}
        <Button variant="primary" onClick={() => onConfirm(items)}>
          Continue
        </Button>
      </div>
    </Card>
  );
};

// ============================================================================
// Step 3: Risks
// ============================================================================
const SEVERITY_CONFIG = {
  high: { color: T.capRed, bg: `${T.capRed}18`, label: 'High' },
  medium: { color: '#C07A1A', bg: '#C07A1A18', label: 'Medium' },
  low: { color: T.moss, bg: `${T.moss}18`, label: 'Low' },
};

const RisksStep = ({ risks, onAcknowledge, onBack, isLoading }) => {
  const [items, setItems] = useState(risks || []);
  const [editingId, setEditingId] = useState(null);
  const [editDesc, setEditDesc] = useState('');
  const [editMitigation, setEditMitigation] = useState('');
  const [adding, setAdding] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [newMitigation, setNewMitigation] = useState('');

  React.useEffect(() => {
    if (risks?.length) setItems(risks);
  }, [risks]);

  const handleDismiss = (id) => {
    setItems(prev => prev.filter(r => r.id !== id));
  };

  const handleEditStart = (r) => {
    setEditingId(r.id);
    setEditDesc(r.description || '');
    setEditMitigation(r.mitigation || '');
  };

  const handleEditSave = (id) => {
    setItems(prev => prev.map(r =>
      r.id === id ? { ...r, description: editDesc, mitigation: editMitigation } : r
    ));
    setEditingId(null);
  };

  const handleAdd = () => {
    if (!newTitle.trim()) return;
    setItems(prev => [...prev, {
      id: `r_custom_${Date.now()}`,
      title: newTitle.trim(),
      description: newDesc.trim(),
      mitigation: newMitigation.trim(),
      severity: 'medium',
      affects_scope: false,
      category: 'technical',
    }]);
    setNewTitle('');
    setNewDesc('');
    setNewMitigation('');
    setAdding(false);
  };

  if (isLoading || !risks?.length) {
    return (
      <Card padding="lg" style={{ backgroundColor: T.card, border: `1px solid ${T.rule}`, boxShadow: T.sh1 }}>
        <div style={{ textAlign: 'center', padding: tokens.spacing[8] }}>
          <Text style={{ color: T.inkMuted }}>
            Analyzing risks and considerations...
          </Text>
        </div>
      </Card>
    );
  }

  return (
    <Card padding="lg" style={{ backgroundColor: T.card, border: `1px solid ${T.rule}`, boxShadow: T.sh1 }}>
      <Text style={{
        fontWeight: tokens.typography.fontWeight.semibold,
        fontSize: tokens.typography.fontSize.lg[0],
        marginBottom: tokens.spacing[2],
        color: T.ink,
        fontFamily: T.hand,
      }}>
        Things to consider
      </Text>
      <Caption style={{ marginBottom: tokens.spacing[4], color: T.inkMuted }}>
        These aren't blockers — just things worth knowing before you start building.
      </Caption>

      {items.map((r) => {
        const sev = SEVERITY_CONFIG[r.severity] || SEVERITY_CONFIG.medium;
        return (
          <div key={r.id} style={{
            padding: tokens.spacing[4],
            backgroundColor: T.paperSoft,
            borderRadius: T.r3,
            border: `1px solid ${T.rule}`,
            borderLeft: `3px solid ${sev.color}`,
            marginBottom: tokens.spacing[3],
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              marginBottom: tokens.spacing[2],
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacing[2] }}>
                <Text style={{
                  margin: 0,
                  fontWeight: tokens.typography.fontWeight.semibold,
                  fontSize: tokens.typography.fontSize.sm[0],
                  color: T.ink,
                }}>
                  {r.title}
                </Text>
                <span style={{
                  fontSize: tokens.typography.fontSize.xs[0],
                  color: sev.color,
                  backgroundColor: sev.bg,
                  padding: `2px ${tokens.spacing[2]}`,
                  borderRadius: T.full,
                }}>
                  {sev.label}
                </span>
                {r.affects_scope && (
                  <span style={{
                    fontSize: tokens.typography.fontSize.xs[0],
                    color: T.orange,
                    backgroundColor: `${T.orange}18`,
                    padding: `2px ${tokens.spacing[2]}`,
                    borderRadius: T.full,
                  }}>
                    Affects scope
                  </span>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacing[1], flexShrink: 0 }}>
                <button
                  onClick={() => handleEditStart(r)}
                  style={{
                    background: 'none',
                    border: `1px solid ${T.rule}`,
                    borderRadius: T.r2,
                    color: T.ink2,
                    padding: `${tokens.spacing[1]} ${tokens.spacing[2]}`,
                    cursor: 'pointer',
                    fontSize: tokens.typography.fontSize.xs[0],
                  }}
                >
                  Edit
                </button>
                <Button variant="ghost" size="sm" onClick={() => handleDismiss(r.id)}
                  style={{ padding: '2px 6px', minWidth: 'auto', color: T.inkMuted }}>
                  &times;
                </Button>
              </div>
            </div>

            {editingId === r.id ? (
              <div>
                <textarea
                  value={editDesc}
                  onChange={(e) => setEditDesc(e.target.value)}
                  rows={3}
                  placeholder="Description..."
                  style={{
                    width: '100%',
                    padding: tokens.spacing[2],
                    backgroundColor: T.card,
                    border: `1px solid ${T.orange}`,
                    borderRadius: T.r2,
                    color: T.ink,
                    fontSize: tokens.typography.fontSize.sm[0],
                    resize: 'vertical',
                    fontFamily: 'inherit',
                    boxSizing: 'border-box',
                    lineHeight: 1.6,
                    outline: 'none',
                    marginBottom: tokens.spacing[2],
                  }}
                  autoFocus
                />
                <textarea
                  value={editMitigation}
                  onChange={(e) => setEditMitigation(e.target.value)}
                  rows={2}
                  placeholder="Mitigation..."
                  style={{
                    width: '100%',
                    padding: tokens.spacing[2],
                    backgroundColor: T.card,
                    border: `1px solid ${T.rule}`,
                    borderRadius: T.r2,
                    color: T.ink,
                    fontSize: tokens.typography.fontSize.sm[0],
                    resize: 'vertical',
                    fontFamily: 'inherit',
                    boxSizing: 'border-box',
                    lineHeight: 1.6,
                    outline: 'none',
                  }}
                />
                <div style={{ display: 'flex', gap: tokens.spacing[2], marginTop: tokens.spacing[2], justifyContent: 'flex-end' }}>
                  <Button variant="ghost" size="sm" onClick={() => setEditingId(null)}>Cancel</Button>
                  <Button variant="primary" size="sm" onClick={() => handleEditSave(r.id)}>Save</Button>
                </div>
              </div>
            ) : (
              <>
                <Text style={{
                  margin: 0,
                  fontSize: tokens.typography.fontSize.sm[0],
                  color: T.ink2,
                  marginBottom: tokens.spacing[2],
                  lineHeight: 1.6,
                }}>
                  {r.description}
                </Text>

                {r.mitigation && (
                  <div style={{
                    padding: tokens.spacing[2],
                    backgroundColor: `${T.moss}0A`,
                    borderRadius: T.r2,
                    border: `1px solid ${T.moss}28`,
                  }}>
                    <Caption style={{ margin: 0, color: T.moss }}>
                      Mitigation: {r.mitigation}
                    </Caption>
                  </div>
                )}
              </>
            )}
          </div>
        );
      })}

      {/* Add risk */}
      {adding ? (
        <div style={{
          padding: tokens.spacing[4],
          backgroundColor: T.paperSoft,
          borderRadius: T.r3,
          border: `1px solid ${T.orange}`,
          marginBottom: tokens.spacing[3],
        }}>
          <input
            type="text"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            placeholder="Risk title..."
            autoFocus
            style={{
              width: '100%',
              padding: tokens.spacing[2],
              backgroundColor: T.card,
              border: `1px solid ${T.rule}`,
              borderRadius: T.r2,
              color: T.ink,
              fontSize: tokens.typography.fontSize.sm[0],
              fontFamily: 'inherit',
              fontWeight: 600,
              outline: 'none',
              boxSizing: 'border-box',
              marginBottom: tokens.spacing[2],
            }}
          />
          <textarea
            value={newDesc}
            onChange={(e) => setNewDesc(e.target.value)}
            rows={2}
            placeholder="Description — why does this matter?"
            style={{
              width: '100%',
              padding: tokens.spacing[2],
              backgroundColor: T.card,
              border: `1px solid ${T.rule}`,
              borderRadius: T.r2,
              color: T.ink,
              fontSize: tokens.typography.fontSize.sm[0],
              resize: 'vertical',
              fontFamily: 'inherit',
              boxSizing: 'border-box',
              lineHeight: 1.6,
              outline: 'none',
              marginBottom: tokens.spacing[2],
            }}
          />
          <textarea
            value={newMitigation}
            onChange={(e) => setNewMitigation(e.target.value)}
            rows={2}
            placeholder="Mitigation (optional)"
            style={{
              width: '100%',
              padding: tokens.spacing[2],
              backgroundColor: T.card,
              border: `1px solid ${T.rule}`,
              borderRadius: T.r2,
              color: T.ink,
              fontSize: tokens.typography.fontSize.sm[0],
              resize: 'vertical',
              fontFamily: 'inherit',
              boxSizing: 'border-box',
              lineHeight: 1.6,
              outline: 'none',
            }}
          />
          <div style={{ display: 'flex', gap: tokens.spacing[2], marginTop: tokens.spacing[2], justifyContent: 'flex-end' }}>
            <Button variant="ghost" size="sm" onClick={() => { setAdding(false); setNewTitle(''); setNewDesc(''); setNewMitigation(''); }}>Cancel</Button>
            <Button variant="primary" size="sm" onClick={handleAdd} disabled={!newTitle.trim()}>Add</Button>
          </div>
        </div>
      ) : (
        <button
          onClick={() => setAdding(true)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: tokens.spacing[1],
            background: 'none',
            border: `1px dashed ${T.rule}`,
            borderRadius: T.r3,
            color: T.inkMuted,
            cursor: 'pointer',
            fontSize: tokens.typography.fontSize.xs[0],
            padding: `${tokens.spacing[2]} ${tokens.spacing[3]}`,
            width: '100%',
            justifyContent: 'center',
            marginBottom: tokens.spacing[3],
            transition: 'all 0.15s ease',
          }}
          onMouseEnter={(e) => { e.target.style.borderColor = T.orange; e.target.style.color = T.orange; }}
          onMouseLeave={(e) => { e.target.style.borderColor = T.rule; e.target.style.color = T.inkMuted; }}
        >
          + Add a risk or consideration
        </button>
      )}

      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        paddingTop: tokens.spacing[4],
        borderTop: `1px solid ${T.rule}`,
      }}>
        {onBack ? (
          <Button variant="ghost" size="sm" onClick={onBack}>Back</Button>
        ) : <div />}
        <Button variant="primary" onClick={() => onAcknowledge(items)}>
          I understand — continue
        </Button>
      </div>
    </Card>
  );
};

// ============================================================================
// Step 4: Scoping
// ============================================================================
const ScopingStep = ({ features, existingFeatures, featureDescriptions = {}, risks, onConfirm, onBack, isLoading, initialPhases }) => {
  const [phases, setPhases] = useState(initialPhases || []);
  const [phasesReady, setPhasesReady] = useState(!!initialPhases?.length);
  const [localFeatures, setLocalFeatures] = useState([...(features || [])]);
  const [localExisting, setLocalExisting] = useState([...(existingFeatures || [])]);
  const [newFeatureInput, setNewFeatureInput] = useState('');
  // dragState.fromPhaseIdx: number = from that phase, null = from unassigned pool, 'existing' = from already-built zone
  // dragState.featureIdx: index into localFeatures when from phase/unassigned, index into localExisting when from 'existing'
  const [dragState, setDragState] = useState(null);
  const [dropTarget, setDropTarget] = useState(null); // phaseIdx | 'unassigned' | 'existing'
  const [editingName, setEditingName] = useState(null);
  const [editNameValue, setEditNameValue] = useState('');

  React.useEffect(() => {
    if (initialPhases?.length) {
      setPhases(initialPhases);
      setPhasesReady(true);
    }
  }, [initialPhases]);

  React.useEffect(() => {
    setLocalExisting([...(existingFeatures || [])]);
  }, [existingFeatures]);

  if (isLoading || !phasesReady) {
    return (
      <Card padding="lg" style={{ backgroundColor: T.card, border: `1px solid ${T.rule}`, boxShadow: T.sh1 }}>
        <div style={{ textAlign: 'center', padding: tokens.spacing[8] }}>
          <Text style={{ color: T.inkMuted }}>
            Recommending phase groupings...
          </Text>
        </div>
      </Card>
    );
  }

  const moveFeature = (featureIdx, fromPhaseIdx, toPhaseIdx) => {
    if (fromPhaseIdx === toPhaseIdx) return;

    if (fromPhaseIdx === 'existing') {
      // Moving out of "Already built" → planning (unassigned pool or a phase)
      const featureName = localExisting[featureIdx];
      if (!featureName) return;
      const newFeatures = [...localFeatures, { name: featureName }];
      const newIdx = newFeatures.length - 1;
      setLocalExisting(prev => prev.filter((_, i) => i !== featureIdx));
      setLocalFeatures(newFeatures);
      if (toPhaseIdx !== null) {
        setPhases(prev => prev.map((p, i) =>
          i === toPhaseIdx ? { ...p, feature_indices: [...p.feature_indices, newIdx] } : p
        ));
      }
      return;
    }

    if (toPhaseIdx === 'existing') {
      // Moving into "Already built" from planning (phase or unassigned pool)
      const featureName = localFeatures[featureIdx]?.name;
      if (!featureName) return;
      setLocalExisting(prev => [...prev, featureName]);
      setPhases(prev => prev.map(p => ({
        ...p,
        feature_indices: p.feature_indices
          .filter(i => i !== featureIdx)
          .map(i => i > featureIdx ? i - 1 : i),
      })).filter(p => p.feature_indices.length > 0));
      setLocalFeatures(prev => prev.filter((_, i) => i !== featureIdx));
      return;
    }

    // Normal move within the planning zone (phase ↔ phase, phase ↔ unassigned)
    setPhases(prev => {
      const updated = prev.map(p => ({ ...p, feature_indices: [...p.feature_indices] }));
      if (fromPhaseIdx !== null) {
        updated[fromPhaseIdx].feature_indices = updated[fromPhaseIdx].feature_indices.filter(i => i !== featureIdx);
      }
      if (toPhaseIdx !== null) {
        if (!updated[toPhaseIdx].feature_indices.includes(featureIdx)) {
          updated[toPhaseIdx].feature_indices.push(featureIdx);
        }
      }
      return updated.filter(p => p.feature_indices.length > 0);
    });
  };

  const deleteFeature = (delIdx) => {
    setLocalFeatures(prev => prev.filter((_, i) => i !== delIdx));
    setPhases(prev => prev.map(phase => ({
      ...phase,
      feature_indices: phase.feature_indices
        .filter(i => i !== delIdx)
        .map(i => i > delIdx ? i - 1 : i),
    })).filter(p => p.feature_indices.length > 0));
  };

  const addFeature = () => {
    const name = newFeatureInput.trim();
    if (!name) return;
    setLocalFeatures(prev => [...prev, { name }]);
    setNewFeatureInput('');
    // New feature lands in unassigned pool (not added to any phase)
  };

  const addPhase = () => {
    const nextNum = phases.length + 1;
    setPhases(prev => [...prev, {
      name: `Phase ${nextNum}`,
      rationale: '',
      feature_indices: [],
      estimated_complexity: 'medium',
    }]);
  };

  const deletePhase = (phaseIdx) => {
    if (phases.length <= 1) return;
    setPhases(prev => {
      const removed = prev[phaseIdx];
      const updated = prev.filter((_, i) => i !== phaseIdx);
      // Move orphaned features to the first remaining phase
      if (removed.feature_indices.length > 0 && updated.length > 0) {
        updated[0] = {
          ...updated[0],
          feature_indices: [...updated[0].feature_indices, ...removed.feature_indices],
        };
      }
      return updated;
    });
  };

  const startRename = (phaseIdx) => {
    setEditingName(phaseIdx);
    setEditNameValue(phases[phaseIdx].name);
  };

  const saveRename = () => {
    if (editingName === null) return;
    setPhases(prev => prev.map((p, i) =>
      i === editingName ? { ...p, name: editNameValue.trim() || p.name } : p
    ));
    setEditingName(null);
  };

  // Drag handlers
  const handleDragStart = (e, featureIdx, fromPhaseIdx) => {
    setDragState({ featureIdx, fromPhaseIdx }); // fromPhaseIdx is null when dragging from unassigned pool
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', '');
  };

  const handleDragOver = (e, target) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDropTarget(target);
  };

  const handleDragLeave = () => {
    setDropTarget(null);
  };

  const handleDrop = (e, toTarget) => {
    e.preventDefault();
    if (dragState) {
      moveFeature(dragState.featureIdx, dragState.fromPhaseIdx, toTarget);
    }
    setDragState(null);
    setDropTarget(null);
  };

  const handleDragEnd = () => {
    setDragState(null);
    setDropTarget(null);
  };

  return (
    <Card padding="lg" style={{ backgroundColor: T.card, border: `1px solid ${T.rule}`, boxShadow: T.sh1 }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: tokens.spacing[2],
      }}>
        <Text style={{
          fontWeight: tokens.typography.fontWeight.semibold,
          fontSize: tokens.typography.fontSize.lg[0],
          margin: 0,
          color: T.ink,
          fontFamily: T.hand,
        }}>
          What to build and when
        </Text>
        <button
          onClick={addPhase}
          style={{
            background: 'none',
            border: `1px solid ${T.rule}`,
            borderRadius: T.r2,
            color: T.ink2,
            cursor: 'pointer',
            padding: `${tokens.spacing[1]} ${tokens.spacing[3]}`,
            fontSize: tokens.typography.fontSize.sm[0],
          }}
        >
          + Add Phase
        </button>
      </div>
      <Caption style={{ marginBottom: tokens.spacing[4], color: T.inkMuted }}>
        Each phase becomes its own plan with features and stories.
        Drag features between phases to decide what ships first.
        Hover over any feature to see its description.
      </Caption>

      <div style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${Math.min(phases.length, 3)}, 1fr)`,
        gap: tokens.spacing[3],
        marginBottom: tokens.spacing[4],
      }}>
        {phases.map((phase, phaseIdx) => {
          const isDropHover = dropTarget === phaseIdx && dragState?.fromPhaseIdx !== phaseIdx;
          return (
            <div
              key={phaseIdx}
              onDragOver={(e) => handleDragOver(e, phaseIdx)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, phaseIdx)}
              style={{
                padding: tokens.spacing[3],
                backgroundColor: isDropHover
                  ? `${T.orange}12`
                  : T.paperDeep,
                borderRadius: T.r3,
                border: `2px solid ${
                  isDropHover
                    ? T.orange
                    : phaseIdx === 0
                      ? T.ruleStrong
                      : T.rule
                }`,
                transition: 'border-color 0.15s, background-color 0.15s',
                minHeight: '100px',
              }}
            >
              {/* Phase header */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: tokens.spacing[2],
              }}>
                {editingName === phaseIdx ? (
                  <input
                    value={editNameValue}
                    onChange={(e) => setEditNameValue(e.target.value)}
                    onBlur={saveRename}
                    onKeyDown={(e) => e.key === 'Enter' && saveRename()}
                    autoFocus
                    style={{
                      background: 'none',
                      border: `1px solid ${T.orange}`,
                      borderRadius: T.r2,
                      color: T.ink,
                      fontSize: tokens.typography.fontSize.sm[0],
                      fontWeight: tokens.typography.fontWeight.semibold,
                      padding: `2px ${tokens.spacing[1]}`,
                      width: '100px',
                      fontFamily: 'inherit',
                    }}
                  />
                ) : (
                  <Text
                    onClick={() => startRename(phaseIdx)}
                    style={{
                      margin: 0,
                      fontWeight: tokens.typography.fontWeight.semibold,
                      fontSize: tokens.typography.fontSize.sm[0],
                      color: phaseIdx === 0 ? T.orange : T.ink,
                      cursor: 'pointer',
                    }}
                    title="Click to rename"
                  >
                    {phase.name}
                  </Text>
                )}

                <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacing[2] }}>
                  <Caption style={{ margin: 0, color: T.inkMuted }}>
                    {(() => { const n = phase.feature_indices.filter(i => localFeatures[i]).length; return `${n} ${n === 1 ? 'feature' : 'features'}`; })()}
                  </Caption>
                  {phases.length > 1 && (
                    <button
                      onClick={() => deletePhase(phaseIdx)}
                      title="Delete phase (features move to first phase)"
                      style={{
                        background: 'none',
                        border: 'none',
                        color: T.inkMuted,
                        cursor: 'pointer',
                        fontSize: '14px',
                        padding: '2px',
                        lineHeight: 1,
                      }}
                    >
                      &times;
                    </button>
                  )}
                </div>
              </div>
              {phase.feature_indices.length > 0 && (
                <Caption style={{
                  margin: 0,
                  marginBottom: tokens.spacing[2],
                  fontSize: '11px',
                  color: T.inkFaint,
                  opacity: 0.7,
                }}>
                  → will generate features & stories
                </Caption>
              )}

              {/* Features */}
              {phase.feature_indices.length === 0 && (
                <div style={{
                  padding: tokens.spacing[4],
                  textAlign: 'center',
                  border: `1px dashed ${T.rule}`,
                  borderRadius: T.r2,
                  color: T.inkFaint,
                  fontSize: tokens.typography.fontSize.xs[0],
                }}>
                  Drop features here
                </div>
              )}

              {phase.feature_indices.map((fIdx) => {
                const feature = localFeatures[fIdx];
                if (!feature) return null;
                const isDragging = dragState?.featureIdx === fIdx && dragState?.fromPhaseIdx === phaseIdx;
                return (
                  <div
                    key={fIdx}
                    draggable
                    onDragStart={(e) => handleDragStart(e, fIdx, phaseIdx)}
                    onDragEnd={handleDragEnd}
                    title={featureDescriptions[feature.name] || undefined}
                    style={{
                      padding: `${tokens.spacing[1]} ${tokens.spacing[2]}`,
                      backgroundColor: T.card,
                      borderRadius: T.r2,
                      marginBottom: tokens.spacing[1],
                      cursor: 'grab',
                      opacity: isDragging ? 0.4 : 1,
                      border: `1px solid ${T.rule}`,
                      fontSize: tokens.typography.fontSize.sm[0],
                      color: T.ink,
                      userSelect: 'none',
                      display: 'flex',
                      alignItems: 'center',
                      gap: tokens.spacing[2],
                    }}
                  >
                    <span style={{ color: T.inkFaint, fontSize: '10px', flexShrink: 0 }}>
                      &#x2630;
                    </span>
                    <span style={{ flex: 1, minWidth: 0 }}>{feature.name || `Feature ${fIdx}`}</span>
                    <button
                      onClick={(e) => { e.stopPropagation(); deleteFeature(fIdx); }}
                      title="Remove feature"
                      style={{
                        background: 'none',
                        border: 'none',
                        color: T.inkMuted,
                        cursor: 'pointer',
                        fontSize: '12px',
                        padding: '0 2px',
                        lineHeight: 1,
                        flexShrink: 0,
                        opacity: 0.6,
                      }}
                    >
                      &times;
                    </button>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>

      {/* Already built — drag features in/out; visible when there are existing features or during a drag */}
      {(localExisting.length > 0 || dragState !== null) && (() => {
        const isExistingHover = dropTarget === 'existing' && dragState?.fromPhaseIdx !== 'existing';
        return (
          <div
            onDragOver={(e) => handleDragOver(e, 'existing')}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, 'existing')}
            style={{
              marginBottom: tokens.spacing[4],
              padding: tokens.spacing[3],
              backgroundColor: isExistingHover
                ? `${T.moss}18`
                : `${T.moss}0A`,
              border: `1px solid ${isExistingHover ? T.moss : T.moss + '40'}`,
              borderRadius: T.r3,
              transition: 'border-color 0.15s, background-color 0.15s',
            }}
          >
            <Caption style={{
              margin: 0,
              marginBottom: tokens.spacing[2],
              color: T.moss,
            }}>
              Already built — drag here to mark as done, drag out to plan
            </Caption>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: tokens.spacing[2], minHeight: '28px' }}>
              {localExisting.length === 0 && (
                <Caption style={{ margin: 0, opacity: 0.5, fontSize: '11px', color: T.inkMuted }}>
                  Drop features here to mark them as already implemented
                </Caption>
              )}
              {localExisting.map((name, i) => {
                const isDragging = dragState?.featureIdx === i && dragState?.fromPhaseIdx === 'existing';
                return (
                  <div
                    key={i}
                    draggable
                    onDragStart={(e) => handleDragStart(e, i, 'existing')}
                    onDragEnd={handleDragEnd}
                    title={featureDescriptions[name] || undefined}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: tokens.spacing[1],
                      padding: `${tokens.spacing[1]} ${tokens.spacing[2]}`,
                      backgroundColor: `${T.moss}12`,
                      border: `1px solid ${T.moss}40`,
                      borderRadius: T.r2,
                      fontSize: tokens.typography.fontSize.sm[0],
                      color: T.moss,
                      cursor: 'grab',
                      userSelect: 'none',
                      opacity: isDragging ? 0.4 : 1,
                    }}
                  >
                    <span style={{ fontSize: '10px', opacity: 0.6 }}>&#x2630;</span>
                    ✓ {name}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* Unassigned features pool */}
      {(() => {
        const assignedIndices = new Set(phases.flatMap(p => p.feature_indices));
        const unassigned = localFeatures.map((f, i) => ({ ...f, idx: i })).filter(({ idx }) => !assignedIndices.has(idx));
        const isUnassignedHover = dropTarget === 'unassigned' && dragState?.fromPhaseIdx !== null;
        return (
          <div
            onDragOver={(e) => handleDragOver(e, 'unassigned')}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, null)}
            style={{
              marginBottom: tokens.spacing[4],
              padding: tokens.spacing[3],
              backgroundColor: isUnassignedHover
                ? `${T.orange}10`
                : T.paperDeep,
              border: `1px dashed ${isUnassignedHover ? T.orange : T.rule}`,
              borderRadius: T.r3,
              transition: 'border-color 0.15s, background-color 0.15s',
            }}
          >
            <Caption style={{ margin: 0, marginBottom: tokens.spacing[2], color: T.inkMuted }}>
              Unassigned features — drag into a phase or remove
            </Caption>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: tokens.spacing[2], marginBottom: tokens.spacing[3] }}>
              {unassigned.length === 0 && (
                <Caption style={{ margin: 0, opacity: 0.5, fontSize: '11px', color: T.inkFaint }}>
                  All features are assigned
                </Caption>
              )}
              {unassigned.map(({ name, idx }) => {
                const isDragging = dragState?.featureIdx === idx && dragState?.fromPhaseIdx === null;
                return (
                  <div
                    key={idx}
                    draggable
                    onDragStart={(e) => handleDragStart(e, idx, null)}
                    onDragEnd={handleDragEnd}
                    title={featureDescriptions[name] || undefined}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: tokens.spacing[1],
                      padding: `${tokens.spacing[1]} ${tokens.spacing[2]}`,
                      backgroundColor: T.card,
                      border: `1px solid ${T.rule}`,
                      borderRadius: T.r2,
                      fontSize: tokens.typography.fontSize.sm[0],
                      color: T.ink,
                      cursor: 'grab',
                      userSelect: 'none',
                      opacity: isDragging ? 0.4 : 1,
                    }}
                  >
                    <span style={{ color: T.inkFaint, fontSize: '10px' }}>&#x2630;</span>
                    <span>{name}</span>
                    <button
                      onClick={() => deleteFeature(idx)}
                      title="Remove feature"
                      style={{
                        background: 'none',
                        border: 'none',
                        color: T.inkMuted,
                        cursor: 'pointer',
                        fontSize: '12px',
                        padding: '0 2px',
                        lineHeight: 1,
                        opacity: 0.6,
                      }}
                    >
                      &times;
                    </button>
                  </div>
                );
              })}
            </div>

            <div style={{ display: 'flex', gap: tokens.spacing[2] }}>
              <input
                value={newFeatureInput}
                onChange={(e) => setNewFeatureInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && addFeature()}
                placeholder="Add a feature..."
                style={{
                  flex: 1,
                  padding: `${tokens.spacing[1]} ${tokens.spacing[2]}`,
                  backgroundColor: T.card,
                  border: `1px solid ${T.rule}`,
                  borderRadius: T.r2,
                  color: T.ink,
                  fontSize: tokens.typography.fontSize.sm[0],
                  fontFamily: 'inherit',
                  outline: 'none',
                }}
              />
              <button
                onClick={addFeature}
                disabled={!newFeatureInput.trim()}
                style={{
                  padding: `${tokens.spacing[1]} ${tokens.spacing[3]}`,
                  backgroundColor: newFeatureInput.trim() ? T.orange : 'transparent',
                  border: `1px solid ${newFeatureInput.trim() ? T.orange : T.rule}`,
                  borderRadius: T.r2,
                  color: newFeatureInput.trim() ? '#fff' : T.inkMuted,
                  cursor: newFeatureInput.trim() ? 'pointer' : 'default',
                  fontSize: tokens.typography.fontSize.sm[0],
                  fontFamily: 'inherit',
                }}
              >
                Add
              </button>
            </div>
          </div>
        );
      })()}

      {/* Summary of what will be created */}
      {(phases.filter(p => p.feature_indices.length > 0).length > 0 || localExisting.length > 0) && (
        <div style={{
          padding: tokens.spacing[3],
          backgroundColor: `${T.orange}08`,
          border: `1px solid ${T.orange}28`,
          borderRadius: T.r3,
          marginBottom: tokens.spacing[4],
        }}>
          <Caption style={{ margin: 0, color: T.ink2 }}>
            {(() => {
              const nonEmpty = phases.filter(p => p.feature_indices.length > 0);
              const totalFeatures = nonEmpty.reduce((sum, p) => sum + p.feature_indices.length, 0);
              const alreadyBuiltCount = localExisting.length;
              const alreadyBuiltSuffix = alreadyBuiltCount > 0
                ? ` + an Already Implemented plan (${alreadyBuiltCount} feature${alreadyBuiltCount === 1 ? '' : 's'} already in your codebase).`
                : '.';
              if (nonEmpty.length === 0 && alreadyBuiltCount > 0) {
                return `This will generate a product spec and an Already Implemented plan documenting the ${alreadyBuiltCount} feature${alreadyBuiltCount === 1 ? '' : 's'} already in your codebase. Add features to a phase above to plan what to build next.`;
              }
              if (nonEmpty.length === 1) {
                return `This will generate a product spec and one plan with ${totalFeatures} feature${totalFeatures === 1 ? '' : 's'} → features and stories${alreadyBuiltSuffix}`;
              }
              return `This will generate a product spec and ${nonEmpty.length} plans (${nonEmpty.map(p => p.name).join(', ')}) with ${totalFeatures} features total → each gets its own features and stories${alreadyBuiltSuffix}`;
            })()}
          </Caption>
        </div>
      )}

      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        paddingTop: tokens.spacing[4],
        borderTop: `1px solid ${T.rule}`,
      }}>
        {onBack ? (
          <Button variant="ghost" size="sm" onClick={onBack}>Back</Button>
        ) : <div />}
        <Button
          variant="primary"
          onClick={() => {
            const phasesWithNames = phases.map(phase => ({
              ...phase,
              feature_names: phase.feature_indices.map(i => localFeatures[i]?.name).filter(Boolean),
            }));
            onConfirm(phasesWithNames, localFeatures.map(f => f.name), localExisting);
          }}
        >
          {(() => {
            const activePlans = phases.filter(p => p.feature_indices.length > 0).length;
            if (activePlans === 0) return 'Generate Spec';
            if (activePlans === 1) return 'Generate Spec & Plan';
            return `Generate Spec & ${activePlans} Plans`;
          })()}
        </Button>
      </div>
    </Card>
  );
};

// ============================================================================
// Shared styles
// ============================================================================
const labelStyle = {
  display: 'block',
  marginBottom: tokens.spacing[1],
  fontSize: tokens.typography.fontSize.sm[0],
  color: T.inkMuted,
};

const textareaStyle = {
  width: '100%',
  padding: tokens.spacing[3],
  backgroundColor: T.card,
  border: `1px solid ${T.rule}`,
  borderRadius: T.r3,
  color: T.ink,
  fontSize: tokens.typography.fontSize.base[0],
  resize: 'vertical',
  fontFamily: 'inherit',
  boxSizing: 'border-box',
  lineHeight: 1.6,
};

// ============================================================================
// Main DiscoveryFlow component
// ============================================================================
const DiscoveryFlow = ({
  idea,
  onPlaybackConfirm,
  onAssumptionsConfirm,
  onRisksAcknowledge,
  onScopingConfirm,
  isLoading,
  assumptions,
  risks,
}) => {
  const posthog = usePostHog();

  // Step override for back navigation (null = use server phase)
  const [stepOverride, setStepOverride] = useState(null);
  // Track that the override was set intentionally by the user going back,
  // so the effect below doesn't immediately clear it.
  const isGoingBack = React.useRef(false);

  // Determine current step based on idea state
  const serverStep = useMemo(() => {
    if (!idea.playback_confirmed) return 'playback';
    if (idea.current_phase === 'assumptions' || idea.current_phase === 'playback') return 'assumptions';
    if (idea.current_phase === 'risks') return 'risks';
    if (idea.current_phase === 'spec_review') return 'spec_review';
    if (idea.current_phase === 'scoping') return 'scoping';
    return 'playback';
  }, [idea.playback_confirmed, idea.current_phase]);

  const currentStep = stepOverride || serverStep;

  // Clear override when the SERVER advances past an override that was set while
  // waiting for a step to complete (forward direction). Never clear when the user
  // deliberately navigated back — they should stay there until they confirm.
  React.useEffect(() => {
    if (stepOverride && !isGoingBack.current) {
      const stepOrder = ['playback', 'assumptions', 'risks', 'spec_review', 'scoping'];
      const overrideIdx = stepOrder.indexOf(stepOverride);
      const serverIdx = stepOrder.indexOf(serverStep);
      if (serverIdx > overrideIdx) {
        setStepOverride(null);
      }
    }
  }, [serverStep, stepOverride]);

  const goBack = (toStep) => {
    isGoingBack.current = true;
    setStepOverride(toStep);
  };

  // When a step's confirm is called, clear both the override and the back flag
  const wrapConfirm = (confirmFn, stepName) => (...args) => {
    isGoingBack.current = false;
    setStepOverride(null);
    if (stepName) posthog?.capture('discovery_step_completed', { step: stepName });
    return confirmFn(...args);
  };

  return (
    <div>
      <StepIndicator currentStep={currentStep} />

      {currentStep === 'playback' && (
        <PlaybackStep
          playback={idea.playback}
          onConfirm={wrapConfirm(onPlaybackConfirm, 'playback')}
          isLoading={isLoading}
        />
      )}

      {currentStep === 'assumptions' && (
        <AssumptionsStep
          assumptions={assumptions || idea.assumptions || idea.discovery_questions}
          onConfirm={wrapConfirm(onAssumptionsConfirm, 'assumptions')}
          onBack={() => goBack('playback')}
          isLoading={isLoading}
        />
      )}

      {currentStep === 'risks' && (
        <RisksStep
          risks={risks || idea.risks}
          onAcknowledge={wrapConfirm(onRisksAcknowledge, 'risks')}
          onBack={() => goBack('assumptions')}
          isLoading={isLoading}
        />
      )}

      {currentStep === 'spec_review' && (
        <Card padding="lg" style={{ backgroundColor: T.card, border: `1px solid ${T.rule}`, boxShadow: T.sh1 }}>
          <div style={{ textAlign: 'center', padding: tokens.spacing[8] }}>
            <div style={{ fontSize: '24px', marginBottom: tokens.spacing[3] }}>📋</div>
            <Text style={{ marginBottom: tokens.spacing[2], color: T.ink }}>
              Generating your product spec...
            </Text>
            <Caption style={{ color: T.inkMuted }}>
              We'll show you the full scope once it's ready.
            </Caption>
          </div>
        </Card>
      )}

      {currentStep === 'scoping' && (
        <ScopingStep
          features={(idea.phase_recommendations?.features || []).map(f => ({ name: f }))}
          existingFeatures={idea.phase_recommendations?.existing_features || []}
          featureDescriptions={Object.fromEntries(
            (idea.idea_spec?.core_features || []).map(f => [f.name, f.description || ''])
          )}
          risks={idea.risks}
          initialPhases={idea.phase_recommendations?.phases || []}
          onConfirm={wrapConfirm(onScopingConfirm, 'scoping')}
          onBack={() => goBack('risks')}
          isLoading={isLoading}
        />
      )}
    </div>
  );
};

export default DiscoveryFlow;
